import { supabase } from '../lib/supabase';

const EDGE_FUNCTION_URL = import.meta.env?.VITE_SUPABASE_URL + '/functions/v1';

class SecurityAlertService {
  // Get alert thresholds for current user
  async getAlertThresholds() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('alert_thresholds')?.select('*')?.eq('user_id', user?.id)?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    
    // Return default thresholds if none exist
    return data || {
      failed_login_attempts: 3,
      failed_login_window_minutes: 15,
      new_device_alert_enabled: true,
      unusual_location_alert_enabled: true,
      location_distance_threshold_km: 500,
      role_escalation_alert_enabled: true,
      permission_grant_alert_enabled: true,
      bulk_permission_threshold: 3,
      password_policy_violation_enabled: true,
      compliance_violation_enabled: true,
      data_access_violation_enabled: true,
      sms_alerts_enabled: false,
      email_alerts_enabled: true,
      sms_phone_number: null,
      alert_email: user?.email
    };
  }

  // Update alert thresholds
  async updateAlertThresholds(thresholds) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('alert_thresholds')?.upsert({
        user_id: user?.id,
        ...thresholds,
        updated_at: new Date()?.toISOString()
      }, { onConflict: 'user_id' })?.select()?.single();

    if (error) throw error;
    return data;
  }

  // Create security alert
  async createAlert({
    alertType,
    severity,
    title,
    description,
    triggerEvent,
    thresholdExceeded = {},
    ipAddress = null,
    deviceInfo = null,
    location = null,
    metadata = {}
  }) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get user's alert preferences
    const thresholds = await this.getAlertThresholds();

    // Create alert record
    const { data: alert, error: alertError } = await supabase?.from('security_alerts')?.insert({
        user_id: user?.id,
        alert_type: alertType,
        severity,
        title,
        description,
        trigger_event: triggerEvent,
        threshold_exceeded: thresholdExceeded,
        ip_address: ipAddress,
        device_info: deviceInfo,
        location,
        metadata
      })?.select()?.single();

    if (alertError) throw alertError;

    // Send notifications if enabled
    if (thresholds?.sms_alerts_enabled || thresholds?.email_alerts_enabled) {
      try {
        await this.sendAlertNotifications({
          alertId: alert?.id,
          userId: user?.id,
          alertType,
          severity,
          title,
          description,
          smsEnabled: thresholds?.sms_alerts_enabled,
          emailEnabled: thresholds?.email_alerts_enabled,
          phoneNumber: thresholds?.sms_phone_number,
          email: thresholds?.alert_email || user?.email
        });
      } catch (notifError) {
        console.error('Failed to send alert notifications:', notifError);
      }
    }

    return alert;
  }

  // Send alert notifications via edge function
  async sendAlertNotifications(alertData) {
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(`${EDGE_FUNCTION_URL}/send-security-alert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify(alertData)
    });

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error.error || 'Failed to send alert notifications');
    }

    return await response?.json();
  }

  // Check for suspicious login patterns
  async checkSuspiciousLogin(ipAddress, deviceInfo, location) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return;

    const thresholds = await this.getAlertThresholds();

    // Check for new device
    if (thresholds?.new_device_alert_enabled) {
      const { data: existingDevices } = await supabase?.from('audit_logs')?.select('device_info')?.eq('user_id', user?.id)?.eq('event_type', 'login')?.eq('success', true)?.limit(50);

      const isNewDevice = !existingDevices?.some(log => log?.device_info === deviceInfo);
      
      if (isNewDevice) {
        await this.createAlert({
          alertType: 'suspicious_login',
          severity: 'medium',
          title: 'New Device Login Detected',
          description: `A login was detected from a new device: ${deviceInfo}`,
          triggerEvent: 'new_device',
          ipAddress,
          deviceInfo,
          location
        });
      }
    }

    // Check for failed login attempts
    const { data: recentFailures } = await supabase?.from('audit_logs')?.select('*')?.eq('user_id', user?.id)?.eq('event_type', 'login')?.eq('success', false)?.gte('created_at', new Date(Date.now() - thresholds.failed_login_window_minutes * 60000)?.toISOString())?.order('created_at', { ascending: false });

    if (recentFailures && recentFailures?.length >= thresholds?.failed_login_attempts) {
      await this.createAlert({
        alertType: 'suspicious_login',
        severity: 'high',
        title: 'Multiple Failed Login Attempts',
        description: `${recentFailures?.length} failed login attempts detected in the last ${thresholds?.failed_login_window_minutes} minutes`,
        triggerEvent: 'failed_login',
        thresholdExceeded: {
          attempts: recentFailures?.length,
          threshold: thresholds?.failed_login_attempts,
          window_minutes: thresholds?.failed_login_window_minutes
        },
        ipAddress,
        deviceInfo,
        location
      });
    }
  }

  // Check for permission changes
  async checkPermissionChange(changeType, details) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return;

    const thresholds = await this.getAlertThresholds();

    if (changeType === 'role_escalation' && thresholds?.role_escalation_alert_enabled) {
      await this.createAlert({
        alertType: 'permission_change',
        severity: 'high',
        title: 'Role Escalation Detected',
        description: `User role changed from ${details?.oldRole} to ${details?.newRole}`,
        triggerEvent: 'role_escalation',
        metadata: details
      });
    }

    if (changeType === 'permission_grant' && thresholds?.permission_grant_alert_enabled) {
      await this.createAlert({
        alertType: 'permission_change',
        severity: 'medium',
        title: 'New Permission Granted',
        description: `Permission "${details?.permission}" granted to user`,
        triggerEvent: 'permission_grant',
        metadata: details
      });
    }
  }

  // Check for policy violations
  async checkPolicyViolation(violationType, details) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return;

    const thresholds = await this.getAlertThresholds();

    if (violationType === 'password_policy' && thresholds?.password_policy_violation_enabled) {
      await this.createAlert({
        alertType: 'policy_violation',
        severity: 'medium',
        title: 'Password Policy Violation',
        description: details?.description || 'Password does not meet security requirements',
        triggerEvent: 'password_policy_violation',
        metadata: details
      });
    }

    if (violationType === 'compliance' && thresholds?.compliance_violation_enabled) {
      await this.createAlert({
        alertType: 'policy_violation',
        severity: 'high',
        title: 'Compliance Violation Detected',
        description: details?.description || 'Action violates compliance policies',
        triggerEvent: 'compliance_violation',
        metadata: details
      });
    }
  }

  // Get security alerts with filters
  async getAlerts({
    alertType = null,
    severity = null,
    acknowledged = null,
    startDate = null,
    endDate = null,
    limit = 50,
    offset = 0
  } = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('security_alerts')?.select('*', { count: 'exact' })?.eq('user_id', user?.id);

    if (alertType) query = query?.eq('alert_type', alertType);
    if (severity) query = query?.eq('severity', severity);
    if (acknowledged !== null) query = query?.eq('acknowledged', acknowledged);
    if (startDate) query = query?.gte('created_at', startDate);
    if (endDate) query = query?.lte('created_at', endDate);

    query = query?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw error;

    return { alerts: data, total: count };
  }

  // Acknowledge alert
  async acknowledgeAlert(alertId, resolutionNotes = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('security_alerts')?.update({
        acknowledged: true,
        acknowledged_at: new Date()?.toISOString(),
        acknowledged_by: user?.id,
        resolution_notes: resolutionNotes
      })?.eq('id', alertId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;
    return data;
  }

  // Get alert statistics
  async getAlertStatistics(days = 30) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)?.toISOString();

    const { data: alerts, error } = await supabase?.from('security_alerts')?.select('alert_type, severity, acknowledged')?.eq('user_id', user?.id)?.gte('created_at', startDate);

    if (error) throw error;

    const stats = {
      total: alerts?.length,
      byType: {},
      bySeverity: {},
      acknowledged: alerts?.filter(a => a?.acknowledged)?.length,
      unacknowledged: alerts?.filter(a => !a?.acknowledged)?.length
    };

    alerts?.forEach(alert => {
      stats.byType[alert.alert_type] = (stats?.byType?.[alert?.alert_type] || 0) + 1;
      stats.bySeverity[alert.severity] = (stats?.bySeverity?.[alert?.severity] || 0) + 1;
    });

    return stats;
  }
}

export default new SecurityAlertService();